import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Warning } from '@material-ui/icons';
import { InventoryCountOrder } from '../../store/config/types';
import { convertConstantLiteralToNormal, formatDate, getUserDisplayName } from '../../utils/string.helpers';
import StatusChip from '../StatusChip/StatusChip';
import './InventoryCountSyncConfirmationDialog.scss';

interface InventoryCountSyncConfirmationDialogProps {
  open: boolean;
  inventoryCountOrder?: InventoryCountOrder;
  processing?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function InventoryCountSyncConfirmationDialog({
  open,
  inventoryCountOrder,
  processing,
  onConfirm,
  onCancel,
}: InventoryCountSyncConfirmationDialogProps) {
  if (!inventoryCountOrder) return null;

  const items = inventoryCountOrder.orderInfo?.items ?? [];

  const isUpdated = (counted?: number, expected?: number) => {
    return counted !== undefined && expected !== undefined && counted !== expected;
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="lg" fullWidth>
      <DialogTitle className="sync-dialog-title">
        <Grid container gap={1} alignItems="center">
          <Grid flexGrow={1}>
            <Grid container alignItems="center">
              <Warning style={{ color: '#ff9800' }} />
              <Typography variant="h6">Sync Confirmation</Typography>
            </Grid>
          </Grid>
          {/* Order Header Info */}
          <Grid>
            <Grid container className="sync-dialog-header-info" gap={2} alignItems="center">
              <Typography variant="body2">
                Date: <strong>{formatDate(inventoryCountOrder.date)}</strong>
              </Typography>
              <StatusChip status={inventoryCountOrder.status ?? ''} />
              <Typography variant="body2">
                User: <strong>{getUserDisplayName(inventoryCountOrder.warehouseUser)}</strong>
              </Typography>
              <Typography variant="body2">
                Warehouse: <strong>{inventoryCountOrder.warehouse?.name ?? ''}</strong>
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent className="sync-dialog-content">
        {/* Product Sections */}
        {items
          .filter(
            (item) =>
              item.additionalData?.confirmZero === true ||
              (item.handlingUnitGroups ?? []).some(
                (hug) => hug.status?.toUpperCase() === 'APPROVED' || hug.additionalData?.systemDefined === true,
              ),
          )
          .map((item) => {
            const allHuGroups = (item.handlingUnitGroups ?? []).filter(
              (hug) => hug.status?.toUpperCase() === 'APPROVED' || hug.additionalData?.systemDefined === true,
            );
            const userHuGroups = allHuGroups.filter((hug) => !hug.additionalData?.systemDefined);
            const systemHuGroups = allHuGroups.filter((hug) => hug.additionalData?.systemDefined === true);
            const hasAnyGroups = userHuGroups.length > 0 || systemHuGroups.length > 0;
            const zeroConfirmed = !item.handlingUnitGroups?.length && item.additionalData?.confirmZero;
            const allRejected =
              !zeroConfirmed && item.handlingUnitGroups?.every((huGroup) => huGroup.status === 'REJECTED');
            return (
              <div key={item.productId} className="sync-dialog-product-section">
                {/* Product Header */}
                <Grid
                  container
                  className={`sync-dialog-product-header${allRejected ? ' all-rejected' : ''}${zeroConfirmed ? ' zero-confirmed' : ''}`}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.product?.name ?? 'Unknown Product'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.product?.description ?? ''}
                    </Typography>
                  </Grid>
                  <Grid item textAlign="right">
                    <Typography variant="body2">
                      Counted:{' '}
                      <strong
                        className={
                          isUpdated(item.totalQuantityCounted, item.totalQuantityExpected)
                            ? 'sync-dialog-updated-value'
                            : ''
                        }
                      >
                        {(item.totalQuantityCounted ?? 0).toFixed(2)}
                      </strong>{' '}
                      {item.product?.unitOfMeasure ?? ''}
                    </Typography>
                    <Typography variant="body2">
                      Expected: <strong>{(item.totalQuantityExpected ?? 0).toFixed(2)}</strong>{' '}
                      {item.product?.unitOfMeasure ?? ''}
                    </Typography>
                  </Grid>
                </Grid>

                {hasAnyGroups ? (
                  <Table size="small" className="sync-dialog-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>ID</TableCell>
                        <TableCell align="right">Counted</TableCell>
                        <TableCell align="right">Expected</TableCell>
                        <TableCell align="right">Difference</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* User-defined HUGs */}
                      {userHuGroups.map((huGroup, idx) => {
                        const difference = (huGroup.quantityCounted ?? 0) - (huGroup.quantityExpected ?? 0);
                        const discrepancy = (huGroup.rate ?? 0) * difference;
                        const unit = huGroup.unit ?? item.product?.unitOfMeasure ?? '';
                        const hasUpdate = isUpdated(huGroup.quantityCounted, huGroup.quantityExpected);

                        return (
                          <TableRow key={`user-${idx}`} className="sync-dialog-user-row">
                            <TableCell>
                              {huGroup.type === 'LOT' ? convertConstantLiteralToNormal(huGroup.type) : ''}
                            </TableCell>
                            <TableCell>{huGroup.type === 'LOT' ? (huGroup.displayName ?? '') : ''}</TableCell>
                            <TableCell align="right">
                              <span className={hasUpdate ? 'sync-dialog-updated-value' : ''}>
                                {(huGroup.quantityCounted ?? 0).toFixed(2)} {unit}
                              </span>
                            </TableCell>
                            <TableCell align="right">
                              {(huGroup.quantityExpected ?? 0).toFixed(2)} {unit}
                            </TableCell>
                            <TableCell align="right">
                              <span className={difference !== 0 ? 'sync-dialog-updated-value' : ''}>
                                {difference.toFixed(2)}
                              </span>
                            </TableCell>
                            <TableCell align="right">{discrepancy.toFixed(2)}</TableCell>
                            <TableCell>
                              <StatusChip status={huGroup.status ?? 'NEW'} small />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* System-defined HUGs — dimmed, empty counted, counted value shown in expected, no diff/amount */}
                      {systemHuGroups.map((huGroup, idx) => {
                        const unit = huGroup.unit ?? item.product?.unitOfMeasure ?? '';

                        return (
                          <TableRow key={`sys-${idx}`} className="sync-dialog-system-row">
                            <TableCell>
                              {huGroup.type === 'LOT' ? convertConstantLiteralToNormal(huGroup.type) : ''}
                            </TableCell>
                            <TableCell>{huGroup.type === 'LOT' ? (huGroup.displayName ?? '') : ''}</TableCell>
                            <TableCell align="right"></TableCell>
                            <TableCell align="right">
                              {(huGroup.quantityCounted ?? 0).toFixed(2)} {unit}
                            </TableCell>
                            <TableCell align="right"></TableCell>
                            <TableCell align="right"></TableCell>
                            <TableCell>System added</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic" p={1}>
                    {allRejected ? 'All rejected' : zeroConfirmed ? 'Zero value confirmed' : 'No handling unit groups'}
                  </Typography>
                )}
              </div>
            );
          })}
      </DialogContent>
      <DialogActions className="sync-dialog-actions">
        <Alert severity="warning" className="sync-dialog-alert">
          <Typography variant="body1" fontWeight="bold" gutterBottom>
            These counts will overwrite existing values in NetSuite. You can still review and edit them during approval.
            Press Sync to continue.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> Inventory adjustments are applied only after the Inventory Count is completed and
            approved in NetSuite.
          </Typography>
        </Alert>
        <Button variant="text" color="error" onClick={onCancel} disabled={processing}>
          Cancel
        </Button>
        <Button variant={processing ? 'text' : 'contained'} color="success" onClick={onConfirm} disabled={processing}>
          {processing ? <CircularProgress size={24} /> : 'Sync'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
